import { loadingPlanTs, timingCls, toTs } from './closeTimeline.mjs';

export const EMPTY_VALUE = '—';

export const PAYMENT_TERM_OPTIONS = [
  '1-3 БД',
  '5-7 БД',
  '10 БД',
  '10-15 БД',
  '15-20 БД',
  '20-25 БД',
  '50/50 1-3 БД',
  '50/50 20-25 БД',
  'Другой',
];

export function prettyPaymentTerm(term) {
  if (!term) return term;
  return String(term).replace(/_50_50/g, ' 50/50');
}

export function toApprovePaymentTerm(term) {
  if (!term) return term;
  return String(term).endsWith('_50_50')
    ? `50/50 ${String(term).slice(0, -'_50_50'.length)}`
    : String(term);
}

export const DAY_CITY_SORTS = [
  { field: 'departure.planed_date_day', dir: 1 },
  { field: 'departure.city_name', dir: 1 },
  { field: 'destination.city_name', dir: 1 },
];

export const PLATFORM_DAY_CITY_SORTS = [
  { field: 'display_name', dir: 1 },
  { field: 'departure.planed_date_day', dir: 1 },
  { field: 'departure.city_name', dir: 1 },
  { field: 'destination.city_name', dir: 1 },
];

export const PLATFORM_TIME_SORTS = [
  { field: 'display_name', dir: 1 },
  { field: 'departure.planed_date', dir: 1 },
];

export const FROM_TO_SORTS = [
  { field: 'departure.city_name', dir: 1 },
  { field: 'destination.city_name', dir: 1 },
];

// Срочность закрытия: часы от закрытия/текущего момента до погрузки
// (asc — самые срочные первыми).
export const CLOSING_URGENCY_SORTS = [
  { field: 'closing_urgency', dir: 1 },
];

export const DISTRIBUTION_STATUSES = [
  { value: 1, key: 'opened', label: 'Открыт', color: '#007bff' },
  { value: 2, key: 'closed', label: 'Закрыт', color: '#28a745' },
  { value: 4, key: 'canceled', label: 'Отменён', color: '#dc3545' },
  { value: 8, key: 'deleted', label: 'Удалён', color: '#6c757d' },
  { value: 16, key: 'outdated', label: 'Устарел', color: '#a87836' },
  { value: 32, key: 'breakdown', label: 'Срыв', color: '#fd7e14' },
  { value: 64, key: 'refusal', label: 'Отказ', color: '#6f42c1' },
];

export function getStatusMeta(order) {
  const status = Number(order?.status);
  return DISTRIBUTION_STATUSES.find((s) => s.value === status)
    || { value: status, key: 'unknown', label: EMPTY_VALUE, color: '#adb5bd' };
}

export const CLIENT_CATEGORY_META = [
  { key: 'top', label: 'ТОП' },
  { key: 'key', label: 'КА' },
  { key: 'regular', label: 'Обычный' },
];

export function getClientCategoryMeta(order) {
  const category = order?.client_category;
  return CLIENT_CATEGORY_META.find((item) => item.key === category) || null;
}

// Статус закрытия карточки: early — закрыт ≥24 ч до погрузки,
// lastday — 12–24 ч, late — <12 ч / не закрыт и просрочено,
// neutral — не закрыт, срок неизвестен или >24 ч.
export function getClosingStatusKey(order) {
  const cls = timingCls(toTs(order?.closed_at), loadingPlanTs(order));
  return cls ? cls.replace('tl-', '') : 'neutral';
}

export function formatDate(value, withYear = false) {
  if (!value) return EMPTY_VALUE;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    ...(withYear ? { year: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateOnly(value, withYear = false) {
  if (!value) return EMPTY_VALUE;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    ...(withYear ? { year: 'numeric' } : {}),
  });
}

export function formatTime(value) {
  if (!value) return EMPTY_VALUE;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;
  return date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatMoney(value) {
  if (typeof value !== 'number') return EMPTY_VALUE;
  return value.toLocaleString('ru-RU');
}

export function parsePriceInput(str) {
  const cleaned = String(str).replace(/\s+/g, '').replace(',', '.');
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function formatPriceField(num) {
  if (num == null || Number.isNaN(Number(num))) return '';
  return Number(num).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function normalizeValue(value) {
  if (value === null || value === undefined) return EMPTY_VALUE;
  const normalized = String(value).trim().replace(/\s+/g, ' ');
  return normalized || EMPTY_VALUE;
}

export function titleCase(value) {
  const normalized = normalizeValue(value);
  if (normalized === EMPTY_VALUE) return normalized;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
}

export function getPoints(order) {
  return order?.order?.route?.points || [];
}

export function getLoadingPoint(order) {
  return getPoints(order)[0];
}

export function getDestinationPoint(order) {
  const points = getPoints(order);
  return points[points.length - 1];
}

export function getExtraLoadingCount(order) {
  const points = getPoints(order);
  if (points.length <= 2) return 0;
  return points.slice(1, -1).filter((point) => point.operation_type !== 'unloading').length;
}

export function getExtraUnloadingCount(order) {
  const points = getPoints(order);
  if (points.length <= 2) return 0;
  return points.slice(1, -1).filter((point) => point.operation_type === 'unloading').length;
}

export function formatCargo(order) {
  const list = order?.order?.cargo?.list;
  if (!Array.isArray(list) || list.length === 0) return EMPTY_VALUE;

  const counts = new Map();
  list.forEach((item) => counts.set(item, (counts.get(item) || 0) + 1));
  return Array.from(counts.entries())
    .map(([item, count]) => (count > 1 ? `${item} (x${count})` : item))
    .join(', ');
}

export function getTariffMeta(order) {
  const approved = order?.approved_result;
  const embedded = order?.embedded_route;
  const hasApproved = approved?.new_cost_without_nds != null || approved?.new_cost_with_nds != null;
  const hasManual = order?.manual_cost != null || order?.manual_cost_nds != null;
  const hasBase = embedded?.cost != null || embedded?.cost_nds != null;

  if (hasApproved) {
    return {
      type: 'Согласованный',
      shortType: 'Сог.',
      left: approved?.new_cost_without_nds ?? null,
      right: approved?.new_cost_with_nds ?? null,
    };
  }
  if (hasManual) {
    return {
      type: 'Ручной',
      shortType: 'Руч.',
      left: order?.manual_cost ?? null,
      right: order?.manual_cost_nds ?? null,
    };
  }
  if (hasBase) {
    return {
      type: 'Базовый',
      shortType: 'Баз.',
      left: embedded?.cost ?? null,
      right: embedded?.cost_nds ?? null,
    };
  }
  return { type: 'Пусто', shortType: EMPTY_VALUE, left: null, right: null };
}

export function formatTariff(order) {
  const tariff = getTariffMeta(order);
  const left = formatMoney(tariff.left);
  const right = formatMoney(tariff.right);
  if (left === EMPTY_VALUE && right === EMPTY_VALUE) return EMPTY_VALUE;
  return `${left} / ${right}`;
}

export function getPaymentType(order) {
  return normalizeValue(order?.approved_result?.new_pay);
}

export function getPaymentTerm(order) {
  return normalizeValue(order?.fact_payment_term || order?.approved_result?.new_pay);
}

export function getExecutor(order) {
  if (order?.worker?.first_name) return order.worker;
  if (order?.internal_booking) return order.internal_booking;
  if (order?.pending?.first_name) return order.pending;
  return null;
}

export function getExecutorRoleMeta(order) {
  if (order?.worker?.first_name) {
    return { key: 'assigned', icon: '🪖', tooltip: 'Взял в работу рейс' };
  }
  if (order?.internal_booking) {
    return { key: 'reserved', icon: '🚚', tooltip: 'Забронировал рейс под наше ТС' };
  }
  if (order?.pending?.first_name) {
    return { key: 'pending', icon: '🕵️', tooltip: 'Начал поиск перевоза на рейс' };
  }
  return null;
}

export function formatUser(user) {
  if (!user) return EMPTY_VALUE;
  return [user.first_name, user.last_name].filter(Boolean).join(' ') || EMPTY_VALUE;
}

function formatTariffForCopy(order) {
  const tariff = getTariffMeta(order);
  const normalizeTariff = (value) => {
    if (value == null) return null;
    const normalized = value >= 1000 ? value / 1000 : value;
    return Number.isInteger(normalized)
      ? `${normalized}`
      : normalized.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
  };
  const left = normalizeTariff(tariff.left);
  const right = normalizeTariff(tariff.right);
  if (left && right) return `${left}/${right}`;
  return left || right || EMPTY_VALUE;
}

export function formatOrderSummary(order) {
  const loading = getLoadingPoint(order);
  const destination = getDestinationPoint(order);
  const cells = [
    normalizeValue(order?.distr_id),
    formatDateOnly(loading?.planed_date, true),
    normalizeValue(loading?.city_name).toUpperCase(),
    normalizeValue(destination?.city_name).toUpperCase(),
    normalizeValue(order?.order?.veh?.type).toUpperCase(),
    formatTariffForCopy(order),
    getPaymentType(order),
    formatTime(loading?.planed_date),
  ];

  return cells.filter((cell) => cell !== EMPTY_VALUE).join(' ');
}

export function getTelegramLink(order) {
  const uid = order?.order?.uid;
  if (!uid) return null;
  const username = process.env.VITE_BOT_USERNAME || process.env.VUE_APP_BOT_USERNAME;
  if (!username) return null;
  return `https://telegram.me/${username}?start=${btoa(`otc_${uid}`)}&`;
}

function normalizeKey(value) {
  return value?.trim?.().toLowerCase?.() || 'unknown';
}

function getDateKey(value) {
  if (!value) return 'unknown';
  const match = String(value).match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  return match || String(value).slice(0, 10);
}

function getHighlightKey(order, mode) {
  const loading = getLoadingPoint(order);
  switch (mode) {
    case 'platform':
      return normalizeKey(String(order?.display_name || order?.order?.meta?.service_name || ''));
    case 'tariff':
      return getTariffMeta(order).type;
    case 'departureCity':
      return normalizeKey(loading?.city_name);
    case 'departureDay':
      return getDateKey(loading?.planed_date);
    case 'urgency':
      return order?.is_critical_closing ? 'critical' : null;
    case 'status': {
      const st = Number(order?.status);
      if (st === 1) return 'opened';
      if (st === 2) return 'closed';
      if (st === 8) return 'deleted';
      if (st === 16) return 'outdated';
      if (st === 32) return 'breakdown';
      if (st === 64) return 'refusal';
      return 'canceled';
    }
    case 'simple':
      return 'simple';
    default:
      return null;
  }
}

export function getHighlightIndexes(orders, mode) {
  if (mode === 'simple') return orders.map((_, index) => index);
  let previousKey = null;
  let currentIndex = -1;
  return orders.map((order) => {
    const key = getHighlightKey(order, mode);
    if (!key) {
      previousKey = null;
      return null;
    }
    if (key !== previousKey) {
      currentIndex = (currentIndex + 1) % 12;
      previousKey = key;
    }
    return currentIndex;
  });
}

export function nextSorts(sorts, field, shiftKey = false) {
  const currentSorts = Array.isArray(sorts) && sorts.length ? sorts : DAY_CITY_SORTS;
  const index = currentSorts.findIndex((item) => item.field === field);
  if (!shiftKey || field === 'worker') {
    const dir = index === 0 && currentSorts[0].dir === 1 ? -1 : 1;
    return [{ field, dir }];
  }
  if (index === -1) return [...currentSorts, { field, dir: 1 }];
  return currentSorts.map((item, i) => (
    i === index ? { ...item, dir: item.dir === 1 ? -1 : 1 } : item
  ));
}

export function sortMeta(sorts, field) {
  const index = (sorts || []).findIndex((item) => item.field === field);
  if (index === -1) return { active: false, label: '' };
  const sort = sorts[index];
  const arrow = sort.dir === 1 ? '↑' : '↓';
  return { active: true, label: `${arrow}${index + 1}` };
}
